import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { sesionCajaService } from '../../services/sesionCajaService';
import { ordenService, OrdenCreate } from '../../services/ordenService';
import { productoService } from '../../services/productoService';
import { cajaService } from '../../services/cajaService';
import { sucursalService } from '../../services/sucursalService';
import { clienteService } from '../../services/clienteService';
import { listaPrecioService } from '../../services/listaPrecioService';
import { impuestoService } from '../../services/impuestoService';
import { reglaPropinaService } from '../../services/reglaPropinaService';
import { ticketService } from '../../services/ticketService';
import { categoriaService } from '../../services/categoriaService';
import { useAuthStore } from '../../store/authStore';
import Carrito from '../../components/POS/Carrito';
import PagoModal from '../../components/POS/PagoModal';
import ClienteSelector from '../../components/POS/ClienteSelector';
import RecuperarOrdenModal from '../../components/POS/RecuperarOrdenModal';
import CategoriaCarousel from '../../components/POS/CategoriaCarousel';

interface CarritoItem {
  productoId: number;
  nombre: string;
  cantidad: number;
  precioUnitario: number;
  descuento?: number;
  subtotal: number;
}

const PuntoVenta = () => {
  const [cajaId, setCajaId] = useState<number | null>(null);
  const [sucursalId, setSucursalId] = useState<number | null>(null);
  const [sesionActiva, setSesionActiva] = useState<any>(null);
  const [carrito, setCarrito] = useState<CarritoItem[]>([]);
  const [showPago, setShowPago] = useState(false);
  const [ordenCreada, setOrdenCreada] = useState<any>(null);
  const [ordenActiva, setOrdenActiva] = useState<any>(null); // Orden que estamos editando
  const [procesarPagoDespues, setProcesarPagoDespues] = useState(false);
  const [clienteSeleccionado, setClienteSeleccionado] = useState<any>(null);
  const [showClienteSelector, setShowClienteSelector] = useState(false);
  const [showRecuperarOrden, setShowRecuperarOrden] = useState(false);
  const [listaPrecioSeleccionada, setListaPrecioSeleccionada] = useState<number | null>(null);
  const [impuestosCalculados, setImpuestosCalculados] = useState<any>(null);
  const [propinaCalculada, setPropinaCalculada] = useState<any>(null);
  const [incluirPropina, setIncluirPropina] = useState(false);
  const [categoriaPadreSeleccionada, setCategoriaPadreSeleccionada] = useState<number | null>(null);
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState<number | null>(null);
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  const { data: cajasData } = useQuery(
    'cajas',
    () => cajaService.getAll(1, 100)
  );

  const { data: sucursalesData } = useQuery(
    'sucursales',
    () => sucursalService.getAll(1, 100)
  );

  // Cargar categorías padre (sin parentId)
  const { data: categoriasPadre } = useQuery(
    ['categorias-padre', user?.tenantId],
    () => categoriaService.getAll(user?.tenantId, null)
  );

  // Cargar categorías hijas cuando se selecciona una categoría padre
  const { data: categoriasHijas } = useQuery(
    ['categorias-hijas', categoriaPadreSeleccionada],
    () => categoriaService.getAll(user?.tenantId, categoriaPadreSeleccionada!),
    { enabled: !!categoriaPadreSeleccionada }
  );

  // Determinar qué categoría usar para filtrar productos
  // Si hay una categoría seleccionada (hija), usarla
  // Si hay una categoría padre seleccionada pero no hay subcategorías, usar la padre
  // Si hay subcategorías pero no hay una seleccionada, no filtrar
  const categoriaParaFiltrar = categoriaSeleccionada || 
    (categoriaPadreSeleccionada && categoriasHijas !== undefined && categoriasHijas.length === 0 
      ? categoriaPadreSeleccionada 
      : null);

  const { data: productosData } = useQuery(
    ['productos', 'pos', categoriaParaFiltrar],
    () => productoService.getAll(1, 200, undefined, undefined, categoriaParaFiltrar || undefined, true)
  );

  const { data: listasPreciosData } = useQuery(
    'listas-precios-activas',
    () => listaPrecioService.getAll(1, 100, true)
  );

  const { data: sesionData } = useQuery(
    ['sesion-activa', cajaId],
    () => sesionCajaService.getActiva(cajaId!),
    {
      enabled: !!cajaId,
      refetchInterval: 30000 // Refrescar cada 30 segundos
    }
  );

  useEffect(() => {
    if (sesionData?.data.sesion) {
      setSesionActiva(sesionData.data.sesion);
    } else {
      setSesionActiva(null);
    }
  }, [sesionData]);

  // Obtener sucursalId de la caja seleccionada
  useEffect(() => {
    if (cajaId && cajasData?.data.cajas) {
      const caja = cajasData.data.cajas.find(c => c.id === cajaId);
      if (caja && caja.sucursalId) {
        setSucursalId(caja.sucursalId);
      }
    }
  }, [cajaId, cajasData]);

  const createOrdenMutation = useMutation(ordenService.create, {
    onSuccess: (data) => {
      toast.success(`Orden ${data.data.orden.numeroOrden} creada correctamente`);
      queryClient.invalidateQueries(['ordenes']);
      queryClient.invalidateQueries(['existencias']);
      
      if (procesarPagoDespues) {
        setOrdenCreada(data.data.orden);
        setShowPago(true);
        setProcesarPagoDespues(false);
      } else {
        setCarrito([]);
      }
      setOrdenActiva(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Error al crear orden');
      setProcesarPagoDespues(false);
    }
  });

  const agregarLineasMutation = useMutation(
    ({ ordenId, data }: { ordenId: number; data: any }) => ordenService.agregarLineas(ordenId, data),
    {
      onSuccess: (data) => {
        toast.success(`Líneas agregadas a orden ${data.data.orden.numeroOrden}`);
        queryClient.invalidateQueries(['ordenes']);
        queryClient.invalidateQueries(['existencias']);
        
        if (procesarPagoDespues) {
          setOrdenCreada(data.data.orden);
          setShowPago(true);
          setProcesarPagoDespues(false);
        } else {
          setCarrito([]);
          setOrdenActiva(null);
        }
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Error al agregar líneas a la orden');
        setProcesarPagoDespues(false);
      }
    }
  );

  const procesarPagoMutation = useMutation(
    ({ ordenId, pagos }: { ordenId: number; pagos: any[] }) => ordenService.procesarPago(ordenId, { pagos }),
    {
      onSuccess: async () => {
        toast.success('Pago procesado correctamente');
        queryClient.invalidateQueries(['ordenes']);
        queryClient.invalidateQueries(['sesiones-caja']);
        
        // Opcional: imprimir ticket automáticamente
        if (ordenCreada) {
          try {
            await ticketService.imprimirTicket(ordenCreada.id, 'html');
          } catch (error) {
            console.error('Error al imprimir ticket:', error);
          }
        }
        
        setShowPago(false);
        setOrdenCreada(null);
        setCarrito([]);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Error al procesar pago');
      }
    }
  );

  const handleAgregarProducto = async (producto: any) => {
    if (!sesionActiva) {
      toast.warning('Debes tener una sesión de caja abierta');
      return;
    }

    // Obtener precio según lista seleccionada
    let precioUnitario = producto.precioBase;
    if (listaPrecioSeleccionada) {
      try {
        const precioData = await listaPrecioService.getPrecioProducto(listaPrecioSeleccionada, producto.id);
        if (precioData.data.fromList) {
          precioUnitario = precioData.data.price;
        }
      } catch (error) {
        // Si falla, usar precio base
        console.error('Error obteniendo precio de lista:', error);
      }
    }

    const itemExistente = carrito.find(item => item.productoId === producto.id);
    
    if (itemExistente) {
      setCarrito(carrito.map(item =>
        item.productoId === producto.id
          ? {
              ...item,
              cantidad: item.cantidad + 1,
              subtotal: (item.cantidad + 1) * item.precioUnitario - (item.descuento || 0)
            }
          : item
      ));
    } else {
      setCarrito([
        ...carrito,
        {
          productoId: producto.id,
          nombre: producto.name,
          cantidad: 1,
          precioUnitario,
          descuento: 0,
          subtotal: precioUnitario
        }
      ]);
    }
  };

  const handleEliminarItem = (productoId: number) => {
    setCarrito(carrito.filter(item => item.productoId !== productoId));
  };

  const handleActualizarCantidad = (productoId: number, cantidad: number) => {
    if (cantidad <= 0) {
      handleEliminarItem(productoId);
      return;
    }

    setCarrito(carrito.map(item =>
      item.productoId === productoId
        ? {
            ...item,
            cantidad,
            subtotal: cantidad * item.precioUnitario - (item.descuento || 0)
          }
        : item
    ));
  };

  // Calcular subtotal y totales
  const subtotal = carrito.reduce((sum, item) => sum + item.subtotal, 0);
  const impuestos = impuestosCalculados?.totalImpuestos || 0;
  const propina = incluirPropina ? (propinaCalculada?.propina || 0) : 0;
  const total = subtotal + impuestos + propina;

  // Calcular impuestos cuando cambia el subtotal
  useEffect(() => {
    const calcularImpuestos = async () => {
      if (subtotal > 0) {
        try {
          const resultado = await impuestoService.calcular(subtotal, 'productos');
          setImpuestosCalculados(resultado.data);
        } catch (error) {
          console.error('Error calculando impuestos:', error);
          setImpuestosCalculados(null);
        }
      } else {
        setImpuestosCalculados(null);
      }
    };

    calcularImpuestos();
  }, [subtotal]);

  // Calcular propina cuando cambia el subtotal o se incluye propina
  useEffect(() => {
    const calcularPropina = async () => {
      if (incluirPropina && subtotal > 0 && sucursalId) {
        try {
          const resultado = await reglaPropinaService.calcular(subtotal, sucursalId);
          setPropinaCalculada(resultado.data);
        } catch (error) {
          console.error('Error calculando propina:', error);
          setPropinaCalculada(null);
        }
      } else {
        setPropinaCalculada(null);
      }
    };

    calcularPropina();
  }, [subtotal, incluirPropina, sucursalId]);

  const handleRecuperarOrden = async (orden: any) => {
    // Cargar las líneas de la orden en el carrito
    const lineasEnCarrito: CarritoItem[] = (orden.lineas || []).map((linea: any) => ({
      productoId: linea.productoId,
      nombre: linea.nombreProducto,
      cantidad: Number(linea.cantidad),
      precioUnitario: Number(linea.precioUnitario),
      descuento: Number(linea.descuento || 0),
      subtotal: Number(linea.subtotal)
    }));

    setCarrito(lineasEnCarrito);
    setOrdenActiva(orden);
    
    // Cargar cliente si existe
    if (orden.cliente) {
      setClienteSeleccionado(orden.cliente);
    }

    toast.info(`Orden ${orden.numeroOrden || orden.id} cargada`);
  };

  const handleCrearOrden = (procesarPago = false) => {
    if (!sesionActiva || !sucursalId || carrito.length === 0) {
      toast.error('Completa todos los campos requeridos');
      return;
    }

    setProcesarPagoDespues(procesarPago);

    const lineas = carrito.map(item => {
      const precioUnitario = Number(item.precioUnitario) || 0;
      return {
        productoId: item.productoId,
        cantidad: Number(item.cantidad),
        ...(precioUnitario > 0 && { precioUnitario }),
        descuento: Number(item.descuento || 0)
      };
    });

    // Si hay una orden activa, agregar líneas; si no, crear nueva orden
    if (ordenActiva) {
      // Agregar líneas a orden existente
      agregarLineasMutation.mutate({
        ordenId: ordenActiva.id,
        data: {
          lineas,
          impuestos: Number(impuestosCalculados?.totalImpuestos || 0),
          descuento: 0
        }
      });
    } else {
      // Crear nueva orden
      const ordenData: OrdenCreate = {
        sesionCajaId: sesionActiva.id,
        sucursalId,
        ...(clienteSeleccionado?.id && { clienteId: clienteSeleccionado.id }),
        lineas,
        impuestos: Number(impuestosCalculados?.totalImpuestos || 0),
        descuento: 0 // Por ahora sin descuentos generales
      };

      createOrdenMutation.mutate(ordenData);
    }
  };

  const handleNuevaOrden = () => {
    setCarrito([]);
    setOrdenActiva(null);
    setClienteSeleccionado(null);
    toast.info('Nueva orden iniciada');
  };

  // Cuando se selecciona una categoría padre, limpiar la selección de categoría hija
  const handleSeleccionarCategoriaPadre = (id: number | null) => {
    setCategoriaPadreSeleccionada(id);
    setCategoriaSeleccionada(null); // Limpiar selección de categoría hija
  };

  // Cuando se selecciona una categoría hija, usarla para filtrar productos
  const handleSeleccionarCategoria = (id: number | null) => {
    setCategoriaSeleccionada(id);
  };

  return (
    <div className="flex h-screen">
      {/* Panel de Productos */}
      <div className="flex-1 p-6 overflow-y-auto">
        <div className="mb-4">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-bold text-gray-800">Punto de Venta</h1>
            {ordenActiva && (
              <div className="flex items-center space-x-2">
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                  Editando: {ordenActiva.numeroOrden || `Orden #${ordenActiva.id}`}
                </span>
                <button
                  onClick={handleNuevaOrden}
                  className="px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm"
                >
                  Nueva Orden
                </button>
              </div>
            )}
          </div>
          
          <div className="flex space-x-4 mb-4">
            <select
              value={cajaId || ''}
              onChange={(e) => {
                setCajaId(e.target.value ? Number(e.target.value) : null);
                setSesionActiva(null);
              }}
              className="px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Selecciona caja</option>
              {cajasData?.data.cajas.map((caja) => (
                <option key={caja.id} value={caja.id}>
                  {caja.name}
                </option>
              ))}
            </select>

            {sesionActiva ? (
              <div className="px-4 py-2 bg-green-100 text-green-800 rounded">
                Sesión Abierta - {sesionActiva.caja?.name}
              </div>
            ) : cajaId ? (
              <div className="px-4 py-2 bg-yellow-100 text-yellow-800 rounded">
                No hay sesión abierta
              </div>
            ) : null}

            <div className="flex items-center space-x-2">
              {clienteSeleccionado ? (
                <div className="flex items-center space-x-2">
                  <span className="px-4 py-2 bg-blue-100 text-blue-800 rounded">
                    {clienteSeleccionado.nombre}
                  </span>
                  <button
                    onClick={() => setClienteSeleccionado(null)}
                    className="text-red-600 hover:text-red-800"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowClienteSelector(true)}
                  className="px-4 py-2 border rounded hover:bg-gray-50"
                >
                  Seleccionar Cliente
                </button>
              )}
            </div>

            {sesionActiva && !ordenActiva && (
              <button
                onClick={() => setShowRecuperarOrden(true)}
                className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700"
              >
                Recuperar Orden
              </button>
            )}

            <div className="flex items-center space-x-2">
              <select
                value={listaPrecioSeleccionada || ''}
                onChange={(e) => {
                  setListaPrecioSeleccionada(e.target.value ? Number(e.target.value) : null);
                  // Limpiar carrito cuando cambia la lista de precios
                  setCarrito([]);
                }}
                className="px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Precio Base</option>
                {listasPreciosData?.data.listas.map((lista) => (
                  <option key={lista.id} value={lista.id}>
                    {lista.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {!sesionActiva ? (
          <div className="text-center py-12 text-gray-500">
            <p className="text-lg">Selecciona una caja y asegúrate de tener una sesión abierta</p>
          </div>
        ) : (
          <>
            {/* Carrusel de Categorías Padre */}
            {categoriasPadre && categoriasPadre.length > 0 && (
              <CategoriaCarousel
                categorias={categoriasPadre}
                categoriaSeleccionada={categoriaPadreSeleccionada}
                onSeleccionar={handleSeleccionarCategoriaPadre}
                label="Categorías"
              />
            )}

            {/* Carrusel de Categorías Hijas */}
            {categoriaPadreSeleccionada && categoriasHijas && categoriasHijas.length > 0 && (
              <CategoriaCarousel
                categorias={categoriasHijas}
                categoriaSeleccionada={categoriaSeleccionada}
                onSeleccionar={handleSeleccionarCategoria}
                label="Subcategorías"
              />
            )}

            {/* Grid de Productos */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mt-4">
              {productosData?.data.productos.filter(p => p.isActive).map((producto) => (
                <button
                  key={producto.id}
                  onClick={() => handleAgregarProducto(producto)}
                  className="p-4 border rounded hover:bg-gray-50 transition text-left hover:shadow-md"
                >
                  {producto.imagenUrl ? (
                    <img 
                      src={producto.imagenUrl} 
                      alt={producto.name}
                      className="w-full h-24 object-cover rounded mb-2"
                    />
                  ) : (
                    <div className="w-full h-24 bg-gray-200 rounded mb-2 flex items-center justify-center">
                      <span className="text-gray-400 text-xs">Sin imagen</span>
                    </div>
                  )}
                  <div className="font-medium text-sm">{producto.name}</div>
                  <div className="text-xs text-gray-500 mt-1">${typeof producto.precioBase === 'number' ? producto.precioBase.toFixed(2) : (Number(producto.precioBase) || 0).toFixed(2)}</div>
                  {producto.sku && (
                    <div className="text-xs text-gray-400 mt-1">SKU: {producto.sku}</div>
                  )}
                </button>
              ))}
              {productosData?.data.productos.filter(p => p.isActive).length === 0 && (
                <div className="col-span-full text-center py-12 text-gray-500">
                  <p>No hay productos disponibles</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Panel de Carrito */}
      <div className="w-96 bg-gray-50 border-l p-6 overflow-y-auto">
        <Carrito
          items={carrito}
          onEliminar={handleEliminarItem}
          onActualizarCantidad={handleActualizarCantidad}
          subtotal={subtotal}
          impuestos={impuestos}
          propina={propina}
          incluirPropina={incluirPropina}
          onTogglePropina={() => setIncluirPropina(!incluirPropina)}
          total={total}
          onCrearOrden={() => handleCrearOrden(false)}
          onCreateOrderAndPay={() => handleCrearOrden(true)}
          disabled={!sesionActiva || carrito.length === 0}
        />
      </div>

      {showPago && ordenCreada && (
        <PagoModal
          orden={ordenCreada}
          onClose={() => {
            setShowPago(false);
            setOrdenCreada(null);
          }}
          onPagar={(pagos) => {
            procesarPagoMutation.mutate({
              ordenId: ordenCreada.id,
              pagos
            });
          }}
        />
      )}

      {showClienteSelector && (
        <ClienteSelector
          onSelect={(cliente) => {
            setClienteSeleccionado(cliente);
            setShowClienteSelector(false);
          }}
          onClose={() => setShowClienteSelector(false)}
        />
      )}

      {showRecuperarOrden && sesionActiva && (
        <RecuperarOrdenModal
          sesionCajaId={sesionActiva.id}
          onClose={() => setShowRecuperarOrden(false)}
          onSeleccionarOrden={handleRecuperarOrden}
        />
      )}
    </div>
  );
};

export default PuntoVenta;

